// src/Blogs/BlogSafetyTraining.jsx
import React, { useEffect, useRef, useState } from "react";

// Images you asked to include (placed inline, not as a gallery)
import book1 from "../assets/BlogImg/CompletedFlowerAndWall.jpeg";
import book2 from "../assets/BlogImg/GrountingFinishingWork.jpeg";
import book3 from "../assets/BlogImg/PritilingPrepration.jpeg";
import book4 from "../assets/BlogImg/TilingLayingProcess.jpeg";

/**
 * Construction Safety Training in India
 * Content is unchanged; images are inserted between sections, no extra copy added.
 */

export default function BlogSafetyTraining() {
  const sections = [
    { id: "why-training", title: "Why Safety Training Matters" },
    { id: "types", title: "Types of Safety Training" },
    { id: "regs", title: "Regulatory & Standards (India)" },
    { id: "audience", title: "Who Should Attend" },
    { id: "benefits", title: "Benefits of Safety Training" },
    { id: "outcomes", title: "Knowledge & Skill Outcomes" },
    { id: "delivery", title: "Training Delivery" },
    { id: "conclusion", title: "Conclusion" },
  ];

  // Reading progress (UI only)
  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const done = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? Math.round((done / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#F7FAFF] via-[#F3F6FB] to-[#F9FBFF]">
      {/* Progress bar */}
      <div className="sticky top-0 z-40 h-1 bg-transparent">
        <div
          className="h-1 bg-gradient-to-r from-sky-500 via-cyan-500 to-indigo-500 transition-[width] duration-150 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
        <div className="relative rounded-[28px] overflow-hidden">
          <div className="absolute -inset-[2px] rounded-[30px] bg-gradient-to-r from-sky-300/50 via-cyan-300/50 to-indigo-400/50 blur-md" />
          <div className="relative rounded-[28px] bg-white/95 ring-1 ring-white/70 backdrop-blur-xl shadow-[0_30px_80px_rgba(2,6,23,.12)] p-6 sm:p-9">
            <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-sky-800/90 font-semibold">
              Construction • Safety • Training
            </p>
            <h1 className="mt-2 text-[26px] sm:text-[34px] lg:text-[38px] font-black leading-tight">
              <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
                Construction Safety Training in India
              </span>
            </h1>
          </div>
        </div>
      </section>

      {/* Body + TOC */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12">
        {/* Article */}
        <article ref={articleRef} className="max-w-3xl w-full justify-self-center lg:justify-self-auto">
          {/* Why Safety Training Matters */}
          <Section id="why-training" title="Why Safety Training Matters">
            <p>
              Safety training in construction reduces incidents, improves site discipline,
              and supports compliance with Indian regulations. It helps protect lives,
              preserves productivity, and sustains project timelines and budgets.
            </p>
            <List
              items={[
                "Fewer accidents and near-misses on site.",
                "Better awareness of hazards and controls.",
                "Improved compliance with statutory requirements.",
                "Lower downtime and reduced costs from rework or stoppages.",
              ]}
            />
          </Section>

          {/* Inline image (after first section) */}
          <InlineImage src={book2} alt="Related visual 1" />

          {/* Types of Safety Training */}
          <Section id="types" title="Types of Safety Training">
            <Subheading>Induction & Daily Briefings</Subheading>
            <List items={["Site Induction for new workers and visitors.", "Toolbox Talks for daily task risk awareness."]} />

            <Subheading>Specialized Work Safety</Subheading>
            <List
              items={[
                "Work at Height & Fall Protection.",
                "Scaffolding safety and inspection basics.",
                "Lifting Operations & Rigging awareness.",
                "Electrical Safety & Lockout/Tagout.",
                "Confined Space Entry procedures.",
                "Hot Work & Fire Watch practices.",
                "Personal Protective Equipment (PPE) use.",
                "First Aid & CPR, Fire Safety & Evacuation.",
              ]}
            />

            <Subheading>Supervisory & Management</Subheading>
            <List
              items={[
                "Hazard Identification & Risk Assessment (HIRA/JSA).",
                "Incident/Accident Reporting & Investigation.",
                "Permit-to-Work systems and control measures.",
                "Emergency Response planning and drills.",
              ]}
            />

            <Subheading>Certification (examples)</Subheading>
            <List items={["NEBOSH / IOSH Managing Safely.", "Work-at-Height, Scaffolding awareness.", "Rigging/Lifting (awareness level).", "Fire Safety, First Aid (basic level)."]} />
          </Section>

          {/* Inline image (between sections) */}
          <InlineImage src={book3} alt="Related visual 2" />

          {/* Regulatory & Standards */}
          <Section id="regs" title="Regulatory & Standards (India)">
            <List
              items={[
                "Building and Other Construction Workers (BOCW) Act & Rules.",
                "Factories Act (where applicable) & State Rules.",
                "Relevant BIS/IS codes and safety guidelines.",
                "Alignment with ISO 45001 (OHSMS) practices where adopted.",
              ]}
            />
          </Section>

          {/* Inline image */}
          <InlineImage src={book4} alt="Related visual 3" />

          {/* Who Should Attend */}
          <Section id="audience" title="Who Should Attend">
            <List
              items={[
                "Workers, Technicians, and Operators.",
                "Supervisors and Foremen.",
                "Engineers and Site Managers.",
                "Safety Officers and Coordinators.",
                "Contractors and Subcontractors.",
                "New Joinees and Visitors (site induction).",
              ]}
            />
          </Section>

          {/* Benefits */}
          <Section id="benefits" title="Benefits of Safety Training">
            <List
              items={[
                "Reduced incidents, improved morale, and safer work culture.",
                "Better quality and fewer reworks due to disciplined execution.",
                "Regulatory compliance and better audit readiness.",
                "Lower project delays from stoppages or investigations.",
              ]}
            />
          </Section>

          {/* Knowledge & Skills */}
          <Section id="outcomes" title="Knowledge & Skill Outcomes">
            <List
              items={[
                "Recognize hazards and apply control measures.",
                "Use PPE correctly and maintain it.",
                "Follow safe systems of work and PTW requirements.",
                "Respond to emergencies and raise alarms properly.",
                "Document incidents and support investigations.",
              ]}
            />
          </Section>

          {/* Training Delivery */}
          <Section id="delivery" title="Training Delivery">
            <List items={["On-site workshops with demonstrations.", "Daily toolbox talks for ongoing reinforcement.", "Scenario-based drills and walkdowns.", "Refresher sessions to sustain awareness."]} />
          </Section>

          {/* Inline image before conclusion */}
          <InlineImage src={book1} alt="Related visual 4" />

          {/* Conclusion */}
          <Section id="conclusion" title="Conclusion" last>
            <p>
              Safety in construction is non-negotiable. Structured training helps teams
              perform with confidence, comply with regulations, and deliver projects
              with fewer incidents and better outcomes.
            </p>
          </Section>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-sm px-3 py-2 rounded-full bg-white text-slate-700 ring-1 ring-slate-200 shadow-sm hover:bg-slate-50"
            >
              ↑ Back to top
            </button>
          </div>
        </article>

        {/* Simple TOC (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl bg-white/95 ring-1 ring-slate-200/70 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-600 mb-2">On this page</p>
            <nav className="space-y-1.5 text-[13px] leading-5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block px-2 py-1 rounded hover:bg-slate-50 text-slate-700 hover:text-sky-700"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </main>
  );
}

/* ---------- Presentational helpers (no content changes) ---------- */

function Section({ id, title, children, last = false }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 mb-6 sm:mb-8 ${last ? "" : "border-b border-slate-200/70 pb-6 sm:pb-8"}`}
    >
      <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-5 sm:p-6">
        <h2 className="text-[20px] sm:text-[22px] font-black tracking-tight mb-2">
          <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-indigo-700 bg-clip-text text-transparent">
            {title}
          </span>
        </h2>
        <div className="text-[15.5px] sm:text-[16px] leading-[1.85] text-slate-800/90 space-y-3">
          {children}
        </div>
      </div>
    </section>
  );
}

function Subheading({ children }) {
  return <h3 className="mt-4 mb-1.5 text-slate-900 font-extrabold text-[16px]">{children}</h3>;
}

function List({ items }) {
  return (
    <ul className="mt-1.5 list-none space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function InlineImage({ src, alt }) {
  return (
    <figure className="my-5 sm:my-6">
      <div className="rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200 shadow-sm">
        <img
          src={src}
          alt={alt}
          className="w-full h-48 sm:h-56 md:h-64 object-cover"
          loading="lazy"
          draggable={false}
        />
      </div>
    </figure>
  );
}

















































// // src/Blogs/BlogSafetyTraining.jsx
// import React, { useEffect, useRef, useState } from "react";

// /**
//  * BlogSafetyTraining.jsx
//  * UI/UX upgrades for readability and navigation:
//  * - Large, comfy typography with better contrast
//  * - Sticky mini-TOC on desktop
//  * - Reading progress bar
//  * - Soft section cards and generous spacing
//  * Content is unchanged from your doc.
//  */

// export default function BlogSafetyTraining() {
//   const sections = [
//     { id: "why-training", title: "Why Safety Training Matters" },
//     { id: "types", title: "Types of Safety Training" },
//     { id: "regs", title: "Regulatory & Standards (India)" },
//     { id: "audience", title: "Who Should Attend" },
//     { id: "benefits", title: "Benefits of Safety Training" },
//     { id: "outcomes", title: "Knowledge & Skill Outcomes" },
//     { id: "delivery", title: "Training Delivery" },
//     { id: "conclusion", title: "Conclusion" },
//   ];

//   // Reading progress
//   const [progress, setProgress] = useState(0);
//   const articleRef = useRef(null);

//   useEffect(() => {
//     const onScroll = () => {
//       const el = articleRef.current;
//       if (!el) return;
//       const rect = el.getBoundingClientRect();
//       const total = el.offsetHeight - window.innerHeight;
//       const done = Math.min(Math.max(-rect.top, 0), total);
//       setProgress(total > 0 ? Math.round((done / total) * 100) : 0);
//     };
//     onScroll();
//     window.addEventListener("scroll", onScroll, { passive: true });
//     return () => window.removeEventListener("scroll", onScroll);
//   }, []);

//   return (
//     <main className="min-h-screen bg-gradient-to-b from-[#F7FAFF] via-[#F3F6FB] to-[#F9FBFF]">
//       {/* Progress bar */}
//       <div className="sticky top-0 z-40 h-1 bg-transparent">
//         <div
//           className="h-1 bg-gradient-to-r from-sky-500 via-cyan-500 to-indigo-500 transition-[width] duration-150 ease-linear"
//           style={{ width: `${progress}%` }}
//         />
//       </div>

//       {/* HERO */}
//       <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
//         <div className="relative rounded-[28px] overflow-hidden">
//           <div className="absolute -inset-[2px] rounded-[30px] bg-gradient-to-r from-sky-300/50 via-cyan-300/50 to-indigo-400/50 blur-md" />
//           <div className="relative rounded-[28px] bg-white/95 ring-1 ring-white/70 backdrop-blur-xl shadow-[0_30px_80px_rgba(2,6,23,.12)] p-6 sm:p-9">
//             <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-sky-800/90 font-semibold">
//               Construction • Safety • Training
//             </p>
//             <h1 className="mt-2 text-[26px] sm:text-[34px] lg:text-[38px] font-black leading-tight">
//               <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
//                 Construction Safety Training in India
//               </span>
//             </h1>

//             {/* Meta chips */}
//             <div className="mt-4 flex flex-wrap gap-2.5">
//               {["Site Safety", "Compliance", "Capability Building"].map((chip) => (
//                 <span
//                   key={chip}
//                   className="text-xs sm:text-[13px] px-3 py-1 rounded-full bg-sky-50 text-sky-800 ring-1 ring-sky-200"
//                 >
//                   {chip}
//                 </span>
//               ))}
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* BODY + SIDEBAR */}
//       <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12">
//         {/* Article */}
//         <article
//           ref={articleRef}
//           className="max-w-3xl w-full justify-self-center lg:justify-self-auto"
//         >
//           <Section id="why-training" title="Why Safety Training Matters">
//             <p>
//               Safety training in construction reduces incidents, improves site discipline,
//               and supports compliance with Indian regulations. It helps protect lives,
//               preserves productivity, and sustains project timelines and budgets.
//             </p>
//             <List
//               items={[
//                 "Fewer accidents and near-misses on site.",
//                 "Better awareness of hazards and controls.",
//                 "Improved compliance with statutory requirements.",
//                 "Lower downtime and reduced costs from rework or stoppages.",
//               ]}
//             />
//           </Section>

//           <Section id="types" title="Types of Safety Training">
//             <Subheading>Induction & Daily Briefings</Subheading>
//             <List
//               items={[
//                 "Site Induction for new workers and visitors.",
//                 "Toolbox Talks for daily task risk awareness.",
//               ]}
//             />

//             <Subheading>Specialized Work Safety</Subheading>
//             <List
//               items={[
//                 "Work at Height & Fall Protection.",
//                 "Scaffolding safety and inspection basics.",
//                 "Lifting Operations & Rigging awareness.",
//                 "Electrical Safety & Lockout/Tagout.",
//                 "Confined Space Entry procedures.",
//                 "Hot Work & Fire Watch practices.",
//                 "Personal Protective Equipment (PPE) use.",
//                 "First Aid & CPR, Fire Safety & Evacuation.",
//               ]}
//             />

//             <Subheading>Supervisory & Management</Subheading>
//             <List
//               items={[
//                 "Hazard Identification & Risk Assessment (HIRA/JSA).",
//                 "Incident/Accident Reporting & Investigation.",
//                 "Permit-to-Work systems and control measures.",
//                 "Emergency Response planning and drills.",
//               ]}
//             />

//             <Subheading>Certification (examples)</Subheading>
//             <List
//               items={[
//                 "NEBOSH / IOSH Managing Safely.",
//                 "Work-at-Height, Scaffolding awareness.",
//                 "Rigging/Lifting (awareness level).",
//                 "Fire Safety, First Aid (basic level).",
//               ]}
//             />
//           </Section>

//           <Section id="regs" title="Regulatory & Standards (India)">
//             <List
//               items={[
//                 "Building and Other Construction Workers (BOCW) Act & Rules.",
//                 "Factories Act (where applicable) & State Rules.",
//                 "Relevant BIS/IS codes and safety guidelines.",
//                 "Alignment with ISO 45001 (OHSMS) practices where adopted.",
//               ]}
//             />
//           </Section>

//           <Section id="audience" title="Who Should Attend">
//             <List
//               items={[
//                 "Workers, Technicians, and Operators.",
//                 "Supervisors and Foremen.",
//                 "Engineers and Site Managers.",
//                 "Safety Officers and Coordinators.",
//                 "Contractors and Subcontractors.",
//                 "New Joinees and Visitors (site induction).",
//               ]}
//             />
//           </Section>

//           <Section id="benefits" title="Benefits of Safety Training">
//             <List
//               items={[
//                 "Reduced incidents, improved morale, and safer work culture.",
//                 "Better quality and fewer reworks due to disciplined execution.",
//                 "Regulatory compliance and better audit readiness.",
//                 "Lower project delays from stoppages or investigations.",
//               ]}
//             />
//           </Section>

//           <Section id="outcomes" title="Knowledge & Skill Outcomes">
//             <List
//               items={[
//                 "Recognize hazards and apply control measures.",
//                 "Use PPE correctly and maintain it.",
//                 "Follow safe systems of work and PTW requirements.",
//                 "Respond to emergencies and raise alarms properly.",
//                 "Document incidents and support investigations.",
//               ]}
//             />
//           </Section>

//           <Section id="delivery" title="Training Delivery">
//             <List
//               items={[
//                 "On-site workshops with demonstrations.",
//                 "Daily toolbox talks for ongoing reinforcement.",
//                 "Scenario-based drills and walkdowns.",
//                 "Refresher sessions to sustain awareness.",
//               ]}
//             />
//           </Section>

//           <Section id="conclusion" title="Conclusion" last>
//             <p>
//               Safety in construction is non-negotiable. Structured training helps teams
//               perform with confidence, comply with regulations, and deliver projects
//               with fewer incidents and better outcomes.
//             </p>
//           </Section>

//           {/* Back to top */}
//           <div className="mt-8 flex justify-end">
//             <button
//               onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
//               className="text-sm px-3 py-2 rounded-full bg-white text-slate-700 ring-1 ring-slate-200 shadow-sm hover:bg-slate-50"
//             >
//               ↑ Back to top
//             </button>
//           </div>
//         </article>

//         {/* Sticky TOC (desktop) */}
//         <aside className="hidden lg:block">
//           <div className="sticky top-24 rounded-2xl bg-white/95 ring-1 ring-slate-200/70 shadow-sm p-4">
//             <p className="text-xs font-semibold text-slate-600 mb-2">On this page</p>
//             <nav className="space-y-1.5 text-[13px] leading-5">
//               {sections.map((s) => (
//                 <a
//                   key={s.id}
//                   href={`#${s.id}`}
//                   className="block px-2 py-1 rounded hover:bg-slate-50 text-slate-700 hover:text-sky-700"
//                 >
//                   {s.title}
//                 </a>
//               ))}
//             </nav>
//           </div>
//         </aside>
//       </div>
//     </main>
//   );
// }

// /* ---------- Presentational helpers (no content changes) ---------- */

// function Section({ id, title, children, last = false }) {
//   return (
//     <section
//       id={id}
//       className={`scroll-mt-28 mb-6 sm:mb-8 ${
//         last ? "" : "border-b border-slate-200/70 pb-6 sm:pb-8"
//       }`}
//     >
//       <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-5 sm:p-6">
//         <h2 className="text-[20px] sm:text-[22px] font-black tracking-tight mb-2">
//           <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-indigo-700 bg-clip-text text-transparent">
//             {title}
//           </span>
//         </h2>
//         <div className="text-[15.5px] sm:text-[16px] leading-[1.85] text-slate-800/90 space-y-3">
//           {children}
//         </div>
//       </div>
//     </section>
//   );
// }

// function Subheading({ children }) {
//   return (
//     <h3 className="mt-4 mb-1.5 text-slate-900 font-extrabold text-[16px]">
//       {children}
//     </h3>
//   );
// }

// function List({ items }) {
//   return (
//     <ul className="mt-1.5 list-none space-y-1.5">
//       {items.map((it, i) => (
//         <li key={i} className="flex items-start gap-3">
//           <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
//           <span>{it}</span>
//         </li>
//       ))}
//     </ul>
//   );
// }
